import { FormEvent, useState } from "react";
import { SignupStepProps, hasServerErrorOn } from "../signupForm";

import { Button } from "../../design-system/button";
import { CheckboxItem } from "../../design-system/checkbox-item";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HeaderLG } from "../../design-system/typography";
import { InputField } from "../../design-system/input-field";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { isValidNpi } from "../signupValidation";
import { lookupNpi } from "../npiLookup";
import { prefillFromLookup } from "../account_info/accountInfo";
import { validateEmail } from "../../util/stringUtil";

const SELLING_POINTS = [
  "Place order in minutes",
  "Verify coverage up front",
  "Track every order through delivery",
];

/** The clinician's first sign-up screen: a short pitch and the "Create an
 * account" panel, which collects the NPI, work email and attestation. */
export const LandingStep = ({
  form,
  updateForm,
  npiLookup,
  setNpiLookup,
  serverErrors = {},
  onNext,
}: SignupStepProps) => {
  // Errors show once the user leaves a field, not while they first type.
  const [touched, setTouched] = useState({ npi: false, email: false });

  const [checking, setChecking] = useState(false);
  // What the last check turned down; cleared when its field changes or on the
  // next check, which reports one field at a time.
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const npiValid = isValidNpi(form.npi);
  // A rough check; the lookup applies the server's rule.
  const emailValid = validateEmail(form.email);
  const canContinue =
    npiValid &&
    emailValid &&
    form.npiAttested &&
    !hasServerErrorOn("landing", serverErrors) &&
    !checking;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canContinue) return;
    setLookupError(null);
    setEmailError(null);
    setChecking(true);
    const result = await lookupNpi(form.npi, form.email);
    setChecking(false);
    if ("emailError" in result) {
      setEmailError(result.emailError);
      return;
    }
    if ("error" in result) {
      setLookupError(result.error);
      return;
    }
    // Same NPI as last time: keep the lookup so step 1 edits are kept.
    if (npiLookup?.npi !== form.npi) {
      setNpiLookup(result.lookup);
      updateForm(prefillFromLookup(result.lookup));
    }
    onNext();
  };

  return (
    <div className="tw-flex tw-w-full tw-items-center tw-gap-[48px] tw-pt-[24px] 720:tw-flex-col">
      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-[10px] tw-leading-[1.4]">
        <p className="tw-m-0 tw-font-sohne-mono tw-text-[14px] tw-tracking-[0.7px] tw-text-verse-text-tertiary">
          CLINICIAN SIGNUP
        </p>
        <HeaderLG>
          Create a Verse Medical account to place your DME order today!
        </HeaderLG>
        <ul className="tw-m-0 tw-mt-[22px] tw-flex tw-list-none tw-flex-col tw-gap-[16px] tw-p-0 tw-font-sohne tw-text-[16px] tw-font-light tw-leading-[1.4] tw-text-verse-text-tertiary">
          {SELLING_POINTS.map((point) => (
            <li key={point} className="tw-flex tw-items-center tw-gap-[10px]">
              <span
                aria-hidden="true"
                className="tw-h-[8px] tw-w-[8px] tw-shrink-0 tw-bg-verse-text-secondary"
              />
              {point}
            </li>
          ))}
        </ul>
      </div>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="tw-flex tw-w-full tw-flex-1 tw-flex-col tw-gap-[16px] tw-rounded-[16px] tw-bg-verse-surface-secondary tw-p-[16px]"
      >
        <h2 className="tw-m-0 tw-text-center tw-font-sohne tw-text-[20px] tw-font-light tw-leading-[1.4] tw-tracking-[-0.2px] tw-text-verse-text-primary">
          Create an account
        </h2>
        <hr className="tw-m-0 tw-w-full tw-border-0 tw-border-t tw-border-solid tw-border-verse-border-primary" />
        {/* Locked while checking, so the answer matches what was checked. */}
        <fieldset
          disabled={checking}
          className="tw-m-0 tw-flex tw-min-w-0 tw-flex-col tw-gap-[24px] tw-border-0 tw-p-0"
        >
          <div className="tw-flex tw-flex-col tw-gap-[16px]">
            <InputField
              id="signup-npi"
              label="Your NPI"
              inputMode="numeric"
              value={form.npi}
              onChange={(npi) => {
                setLookupError(null);
                updateForm({ npi: npi.trim() });
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, npi: true }))}
              error={
                lookupError ??
                serverErrors.npi ??
                (touched.npi && !npiValid
                  ? "Please enter a valid NPI to proceed"
                  : null)
              }
            />
            <InputField
              id="signup-email"
              label="Your Work Email"
              type="email"
              autoComplete="email"
              placeholder="example@organization.com"
              value={form.email}
              onChange={(email) => {
                setEmailError(null);
                updateForm({ email: email.trim() });
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
              error={
                emailError ??
                serverErrors.email ??
                (touched.email && !emailValid
                  ? "Please enter a valid email to proceed"
                  : null)
              }
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-[16px]">
            <CheckboxItem
              id="signup-npi-attested"
              checked={form.npiAttested}
              onChange={(npiAttested) => updateForm({ npiAttested })}
            >
              I attest that I am not using another person’s NPI without
              authorization.
            </CheckboxItem>
            <Button
              type="submit"
              colorScheme="tide"
              size="md"
              className="tw-w-full"
              disabled={!canContinue}
            >
              {checking ? "Checking…" : "Continue"}
              <FontAwesomeIcon
                icon={faArrowRight}
                className="tw-h-[16px] tw-w-[16px]"
              />
            </Button>
          </div>
        </fieldset>
      </form>
    </div>
  );
};
